import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageDwell } from '../api';
import { useDwellDetector } from './useDwellDetector';
import { matchDwellPage, type DwellPageMatch } from './dwellPages';
import { NudgeStack } from './NudgeStack';
import { useProactiveNudgePoller } from './useProactiveNudgePoller';

export function DwellDetector() {
  const location = useLocation();
  const entityId = matchDwellPage(location.pathname)?.entityId || undefined;

  // Page-dwell only *triggers* the backend task (POST the signal). Surfacing the
  // resulting message — and any message from non-dwell triggers — is handled by
  // useProactiveNudgePoller, scoped to the entity currently being viewed.
  const onDwell = useCallback(
    async (match: DwellPageMatch): Promise<boolean> => {
      try {
        await trackPageDwell({
          data: {
            signal_id: crypto.randomUUID(),
            page_key: match.pageKey,
            entity: { type: match.entityType, id: match.entityId },
            dwell_seconds: 60,
            url: location.pathname,
            occurred_at: new Date().toISOString(),
          },
        });
        return true;
      } catch {
        // Fail silently, but release the cooldown so a later dwell can retry.
        return false;
      }
    },
    [location.pathname]
  );

  // No feature-flag gate: proactive nudges run for all cowbell users — the
  // subtree only mounts behind `actor.isCowbell` in ConsoleLayout.
  useDwellDetector({ enabled: true, pathname: location.pathname, onDwell });
  useProactiveNudgePoller(entityId);
  return <NudgeStack />;
}
